import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  MessageSquare, 
  Bell, 
  BarChart3, 
  Clock, 
  FileText,
  Zap,
  Shield,
  Smartphone,
  RefreshCw,
  Search
} from 'lucide-react';

export const FlowLedger = () => {
  const FLOWLEDGER_LOGO = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";
  const FLOWLEDGER_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";
  
  const features = [
    {
      icon: <MessageSquare className="w-6 h-6" strokeWidth={1.5} />,
      title: "SMS Parsing",
      description: "Automatically parse and extract transaction details from mobile money SMS messages."
    },
    {
      icon: <Bell className="w-6 h-6" strokeWidth={1.5} />,
      title: "Instant Alerts",
      description: "Get real-time notifications for every transaction on your mobile money account."
    },
    {
      icon: <BarChart3 className="w-6 h-6" strokeWidth={1.5} />,
      title: "Business Analytics",
      description: "Track your transaction volume, trends, and business performance over time."
    },
    {
      icon: <FileText className="w-6 h-6" strokeWidth={1.5} />,
      title: "Transaction History",
      description: "Complete searchable history of all transactions with export capabilities."
    },
    {
      icon: <Clock className="w-6 h-6" strokeWidth={1.5} />,
      title: "Real-Time Sync",
      description: "Transactions appear in your dashboard within seconds of completion."
    },
    {
      icon: <Search className="w-6 h-6" strokeWidth={1.5} />,
      title: "Smart Search",
      description: "Find any transaction instantly by amount, date, sender, or reference."
    }
  ];

  const benefits = [
    "Never miss a payment confirmation again",
    "Reduce disputes with customers",
    "Track business performance in real-time",
    "Export reports for accounting",
    "Access from any device, anywhere",
    "Bank-level security for your data"
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Connect Your Account",
      description: "Link your mobile money account to FlowLedger through our secure integration."
    },
    {
      step: "02",
      title: "Automatic Tracking",
      description: "Every transaction is automatically captured, parsed, and stored securely."
    },
    {
      step: "03",
      title: "Get Notified",
      description: "Receive instant alerts and confirmations for all your transactions."
    },
    {
      step: "04",
      title: "Analyze & Grow",
      description: "Use insights and analytics to make better business decisions."
    }
  ];

  return (
    <div data-testid="flowledger-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-sky-50 to-white relative overflow-hidden">
        <div className="absolute top-20 right-0 w-96 h-96 bg-sky-100 rounded-full blur-3xl opacity-50" />
        
        <div className="container-main relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <img 
                  src={FLOWLEDGER_LOGO} 
                  alt="FlowLedger" 
                  className="h-16 sm:h-20 object-contain"
                  data-testid="flowledger-logo"
                />
              </div>
              
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Real-time transaction monitoring and confirmation for businesses using mobile money. 
                Never miss a payment again.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/contact?type=demo">
                  <Button className="btn-primary" data-testid="flowledger-demo-btn">
                    Request Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="btn-secondary" data-testid="flowledger-pricing-btn">
                    View Pricing
                  </Button>
                </Link>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <span className="text-sm text-slate-600">
                  <strong className="text-slate-900">500+</strong> businesses trust FlowLedger
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-slate-900">Recent Transactions</h3>
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    Live
                  </span>
                </div>
                
                <div className="space-y-4">
                  {[
                    { type: "Received", amount: "LRD 5,000", from: "John D.", time: "2 min ago", status: "success" },
                    { type: "Received", amount: "LRD 12,500", from: "Mary K.", time: "15 min ago", status: "success" },
                    { type: "Sent", amount: "LRD 3,000", from: "Supplier", time: "1 hour ago", status: "success" },
                    { type: "Received", amount: "LRD 8,750", from: "Customer", time: "2 hours ago", status: "success" },
                  ].map((tx, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.type === "Received" ? "bg-green-100" : "bg-blue-100"
                        }`}>
                          {tx.type === "Received" ? (
                            <ArrowRight className="w-5 h-5 text-green-600 rotate-[-135deg]" />
                          ) : (
                            <ArrowRight className="w-5 h-5 text-blue-600 rotate-45" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{tx.amount}</div>
                          <div className="text-xs text-slate-500">{tx.from}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">{tx.time}</div>
                        <div className="text-xs text-green-600 flex items-center gap-1 justify-end">
                          <CheckCircle2 className="w-3 h-3" />
                          Confirmed
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white" data-testid="flowledger-features">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Everything You Need to Track Transactions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Powerful features designed for businesses that rely on mobile money.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card"
                data-testid={`flowledger-feature-${index}`}
              >
                <div className="icon-container mb-6 bg-sky-50 text-[#0077B6]">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding bg-slate-50" data-testid="flowledger-how-it-works">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">
              Get Started in Minutes
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative" data-testid={`flowledger-step-${index}`}>
                <div className="text-6xl font-bold text-slate-100 mb-4">{step.step}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.description}</p>
                
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 w-8">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white" data-testid="flowledger-benefits">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
                Benefits
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Why Businesses Choose FlowLedger
              </h2>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact?type=demo" className="inline-block mt-8">
                <Button className="btn-primary" data-testid="flowledger-benefits-cta">
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-sky-50 rounded-2xl p-6">
                <RefreshCw className="w-8 h-8 text-[#0077B6] mb-4" strokeWidth={1.5} />
                <div className="text-3xl font-bold text-[#0A1628] mb-1">99.9%</div>
                <div className="text-slate-600 text-sm">Uptime SLA</div>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-6">
                <Zap className="w-8 h-8 text-emerald-600 mb-4" strokeWidth={1.5} />
                <div className="text-3xl font-bold text-[#0A1628] mb-1">&lt;1s</div>
                <div className="text-slate-600 text-sm">Alert Delivery</div>
              </div>
              <div className="bg-slate-100 rounded-2xl p-6">
                <Shield className="w-8 h-8 text-slate-600 mb-4" strokeWidth={1.5} />
                <div className="text-3xl font-bold text-[#0A1628] mb-1">256-bit</div>
                <div className="text-slate-600 text-sm">Encryption</div>
              </div>
              <div className="bg-cyan-50 rounded-2xl p-6">
                <Smartphone className="w-8 h-8 text-[#00B4D8] mb-4" strokeWidth={1.5} />
                <div className="text-3xl font-bold text-[#0A1628] mb-1">24/7</div>
                <div className="text-slate-600 text-sm">Mobile Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0077B6] to-[#00B4D8]" data-testid="flowledger-cta">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to Track Your Transactions?
            </h2>
            <p className="text-blue-100 text-lg mb-10 leading-relaxed">
              Join hundreds of businesses already using FlowLedger to manage their mobile money operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=demo">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-6 font-semibold" data-testid="flowledger-cta-demo">
                  Request Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-6" data-testid="flowledger-cta-pricing">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlowLedger;
